import type { IPatternMapEntry, TToolFamily } from "../types/approval";

export const APPROVAL_SUMMARIZER_NS = "approval_summarizer" as const;

// NOTE: Depth cap for deepSortKeys serialization used in duplicate detection.
// Tool args are typically 2-3 levels deep in practice, so any difference beyond
// this depth is not meaningful for duplicate detection. The cap also prevents
// stack overflow on deeply nested payloads (e.g. rich-text document trees) and
// keeps serialization cost bounded for large tool arguments.
export const DEEP_SORT_MAX_DEPTH = 5;

// NOTE: Maps pattern types to their i18n key suffix and fallback label (singular).
export const PATTERN_SINGULAR_MAP: Record<string, IPatternMapEntry> = {
  daily: { key: "daily", fallback: "Daily" },
  weekly: { key: "weekly", fallback: "Weekly" },
  absoluteMonthly: { key: "monthly", fallback: "Monthly" },
  absoluteYearly: { key: "yearly", fallback: "Yearly" },
};

// NOTE: Maps pattern types to their plural i18n key suffix and fallback template.
export const PATTERN_PLURAL_MAP: Record<string, IPatternMapEntry> = {
  daily: { key: "every_n_days", fallback: "days" },
  weekly: { key: "every_n_weeks", fallback: "weeks" },
  absoluteMonthly: { key: "every_n_months", fallback: "months" },
  absoluteYearly: { key: "every_n_years", fallback: "years" },
};

// NOTE: Maps Google Calendar RRULE FREQ values to Microsoft-style pattern types for display.
export const RRULE_FREQ_TO_PATTERN_TYPE: Record<string, string> = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "absoluteMonthly",
  YEARLY: "absoluteYearly",
};

// NOTE: Maps Google Calendar RRULE BYDAY abbreviations to full day names (formatRecurrencePattern).
export const RRULE_DAY_MAP: Record<string, string> = {
  SU: "sunday",
  MO: "monday",
  TU: "tuesday",
  WE: "wednesday",
  TH: "thursday",
  FR: "friday",
  SA: "saturday",
};

// NOTE: How long a stopped-chat flag stays active before auto-expiring (prevents memory leak)
export const STOPPED_CHAT_EXPIRY_MS = 30_000 as const;

export const MILLISECONDS_PER_MINUTE = 60_000 as const;
export const MS_PER_SECOND = 1_000 as const;
export const MINUTES_PER_HOUR = 60 as const;
export const SECONDS_PER_MINUTE = 60 as const;
export const HOURS_PER_DAY = 24 as const;
export const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
export const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
// NOTE: Approximates a month as 30 days for display purposes only — not calendar-accurate.
export const SECONDS_PER_MONTH = 30 * SECONDS_PER_DAY;

// NOTE: Strips :root { ... } from <style> nodes to avoid DOMPurify allowing global style leakage.
export const REGEX_ROOT_STYLE = /:root\s*{[\s\S]*?}/g;

export const CALENDAR_ISO_DATE_TIME_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

export const CALENDAR_TITLE_LABELS = [
  "summary",
  "title",
  "subject",
  "eventSubject",
  "calendarDisplayName",
] as const;

export const CALENDAR_LABELS_USED_IN_ORDER = new Set([
  ...CALENDAR_TITLE_LABELS,
  "eventSubject",
  "calendarDisplayName",
  "start",
  "end",
  "startTime",
  "endTime",
  "timeZone",
  "location",
  "attendees",
  "requiredAttendees",
  "invitees",
  "addMeetLink",
  "isOnlineMeeting",
  "recurrence",
  "description",
  "body",
  "bodyHtml",
  "content",
  "email",
]);

export const EMAIL_APPROVAL_LABEL_ORDER = [
  "messageSubject",
  "toRecipients",
  "to",
  "cc",
  "bcc",
  "subject",
  "summary",
  "body",
  "bodyHtml",
  "description",
];

export const KEYS_RENDER_AS_HTML = ["bodyHtml", "body", "forwardNote"];

// NOTE: These keys contain long-form content that should not be truncated to MAX_STRING_LENGTH.
export const KEYS_LONG_TEXT = ["prompt", "instructions", "content"];

// NOTE: Maps automation event type raw values to human-readable descriptions for the approval popup.
export const EVENT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  "chat.created": "A new chat conversation is created",
  "chat.message.sent": "A message is sent in a chat",
  "integration.connected": "A new integration is connected",
  "integration.disconnected": "An integration is disconnected",
  "cron.run.completed": "A scheduled task completes successfully",
  "cron.run.failed": "A scheduled task fails",
  "webhook.run.completed": "An incoming trigger completes successfully",
  "webhook.run.failed": "An incoming trigger fails",
  "hook.run.completed": "An event rule completes successfully",
  "hook.run.failed": "An event rule fails",
  "workflow.run.completed": "A workflow completes successfully",
  "workflow.run.failed": "A workflow fails",
  "project.member.added": "A member is added to the project",
  "project.member.removed": "A member is removed from the project",
  "meeting.processed":
    "A Call Assistant meeting finishes processing successfully",
  "meeting.failed": "A Call Assistant meeting fails to produce a result",
};

// NOTE: Splits before each capital letter so "someKey" → ["some", "Key"].
export const REGEX_SPLIT_BEFORE_CAPITAL = /(?=[A-Z])/;

export const MAX_PREVIEW_ROWS = 4;
export const MAX_PREVIEW_COLS = 5;
export const MAX_CELL_LENGTH = 30;

// NOTE: How many of a batch's targets are named before the rest become "... and N more".
// Batch tools reach 500 ids and a `long-text` row has no height cap, so an uncapped list
// would push Approve/Deny off the card. Three names identify WHAT; the count carries HOW MANY.
export const BATCH_NAME_PREVIEW_LIMIT = 3;

// NOTE: Per-name cap in that preview, so one long subject cannot wrap the whole list.
export const BATCH_NAME_MAX_LENGTH = 80;

export const MAX_STRING_LENGTH = 200;

// NOTE: Minimum string length to consider a value as potentially ID-like for truncation.
export const MIN_ID_DISPLAY_LENGTH = 30;

// NOTE: Keys holding a snippet of longer content, shown only so the user can recognize which
// item an opaque id refers to (e.g. which Teams message is being deleted). The tool schemas
// ask the model for a short snippet, but that is only an instruction — these are capped on
// render so a model that sends the whole message body cannot make the card grow.
export const PREVIEW_KEYS = new Set(["messagePreview"]);

// NOTE: Cap for PREVIEW_KEYS — enough to recognize a message, short enough to stay on one
// or two lines above the Approve/Deny buttons. Deliberately well under MAX_STRING_LENGTH.
export const PREVIEW_MAX_LENGTH = 80;

// NOTE: Keys whose values should render in monospace code font (file names, paths, queries).
export const CODE_DISPLAY_KEYS = new Set([
  "filename",
  "filenames",
  "itemname",
  "foldername",
  "parentfoldername",
  "destinationfoldername",
  "newname",
  "pdffilename",
  "query",
  "filterquery",
  "filter",
  "sourcefolderids",
  "address",
  "sourcedata",
]);

export const ID_LIKE_KEYS = new Set([
  "messageid",
  "threadid",
  "draftid",
  "labelid",
  "eventid",
  "taskgid",
  "storygid",
  "issueid",
  "commentid",
  "projectid",
  "recordid",
  "meetingid",
  "webinarid",
  "recordingid",
  "channelid",
  "memberid",
  "itemid",
  "contactid",
  "uuid",
  "id",
  "ids",
  "entryids",
  "recordids",
]);

// NOTE: Keys that should display as "Account" instead of their raw label.
export const ACCOUNT_LIKE_KEYS = new Set([
  "email",
  "azureprincipalname",
  "mailboxuser",
  "integrationtitle",
  // NOTE: Pipedream's injected account selector — PIPEDREAM_ACCOUNT_ARG
  // ("account") and its collision fallback ("nodeshiftAccount"). Deliberately
  // shown, never hidden: which account executes the action is exactly what an
  // approval decision is about. Entries must be lowercase (lookup lowercases).
  "account",
  "nodeshiftaccount",
]);

// NOTE: Pipedream registry prop names that differ from the arg keys the
// approval summarizers were built around. Applied as a pre-normalization in
// formatArgsToRows, so every downstream label consumer (date formatting in
// normalizeRow, the calendar card's start/end/video items) sees the canonical
// key. Keep this map GLOBALLY collision-free: each source key must appear in
// no native tool schema (grep-verified), because the rename is applied without
// knowing the tool name.
export const PIPEDREAM_ARG_KEY_ALIASES: Readonly<Record<string, string>> = {
  eventStartDate: "start",
  eventEndDate: "end",
  createMeetRoom: "addMeetLink",
};

// NOTE: F14 (NSAI-3765) — Pipedream's Microsoft Outlook mail tools carry email
// fields under DIFFERENT keys than the native/Gmail tools the email approval
// card was built around (recipients vs to, content vs body, files vs
// fileNames). Renaming them to the canonical keys makes the Outlook card render
// identically — recipients under the "To" label, content hidden-inline as the
// body, and `files` surfaced as attachment cards (the same AttachmentCard path
// gmail's fileNames uses). This map is applied ONLY to Outlook mail tools
// (canonicalizeApprovalArgs scopes by the `microsoft_outlook-` prefix), so it is
// NOT bound by the global collision-free rule that governs
// PIPEDREAM_ARG_KEY_ALIASES above — `content` here always means the email body
// because the only tools it is applied to are Outlook's send/draft/reply.
export const OUTLOOK_PIPEDREAM_EMAIL_ARG_ALIASES: Readonly<
  Record<string, string>
> = {
  recipients: "to",
  ccRecipients: "cc",
  bccRecipients: "bcc",
  content: "body",
  files: "fileNames",
};

// NOTE: NSAI-7339 — the Pipedream Zoho mail-sending tools carry recipients and
// body under their own keys (Zoho Mail: toAddress / ccAddress / bccAddress /
// content; Zoho Desk's reply: to / content). Renamed to the canonical keys so
// both render in the email card like Gmail. Like the Outlook map above, scoped
// to exactly the tools in ZOHO_PIPEDREAM_EMAIL_TOOLS (see
// canonicalizeApprovalArgs), never applied globally — `content` means page
// content elsewhere.
export const ZOHO_PIPEDREAM_EMAIL_ARG_ALIASES: Readonly<
  Record<string, string>
> = {
  toAddress: "to",
  ccAddress: "cc",
  bccAddress: "bcc",
  content: "body",
};

export const ZOHO_PIPEDREAM_EMAIL_TOOLS: readonly string[] = [
  "zoho_mail-send-email",
  "zoho_desk-send-email-reply",
];

export const BATCH_ID_KEYS = [
  "ids",
  "entryIds",
  "recordIds",
  "messageIds",
  "eventIds",
  "itemIds",
] as const;

// NOTE: Maps batch ID array keys to their corresponding display name array keys.
// When both are present, the display names are shown instead of opaque IDs.
export const BATCH_ID_TO_DISPLAY_KEY: Record<string, string> = {
  messageIds: "messageSubjects",
  eventIds: "eventSubjects",
  itemIds: "itemNames",
  ids: "names",
  entryIds: "entryNames",
  recordIds: "recordNames",
} as const;

// NOTE: Scalar analogue of BATCH_ID_TO_DISPLAY_KEY: maps an opaque recipient/entity ID
// arg key (a user/channel/chat/group ID, a file/folder/message/event ID, an automation
// UUID, etc.) to the readable field that names the SAME target. Tools pass that readable
// name alongside the ID (the model already saw it when it looked the target up), and the
// approval card hides the raw ID row only when its counterpart is present and non-empty —
// showing the recognizable name in its place. When no counterpart exists (a lone ID with
// no name), the ID is kept so the card never hides what an irreversible action targets.
// A value may list SEVERAL candidate keys when the same ID is named differently depending
// on the tool: the first one carrying a readable value wins.
export const ID_TO_DISPLAY_NAME_KEY: Record<
  string,
  string | readonly string[]
> = {
  // NOTE: Recipient / conversation IDs (Slack, Teams, Telegram, Zoom).
  slackUserId: "userName",
  channelId: "channelName",
  usergroupId: "usergroupName",
  chatId: "chatName",
  memberUserId: "memberName",
  // NOTE: Teams removes a member by the MEMBERSHIP id (not the user id), and Zoom by
  // `memberId` — both name the same person, so they share the `memberName` counterpart.
  membershipId: "memberName",
  memberId: "memberName",
  teamId: "teamName",
  // NOTE: Telegram member actions address the person via `targetUserId`.
  targetUserId: "targetUserName",
  // NOTE: Zoom `sendChatMessage` targets a channel via `toChannel` (not `channelId`).
  toChannel: "toChannelName",
  // NOTE: Zoom meetings & webinars — the id is a bare number, the topic names it.
  meetingId: "meetingTopic",
  webinarId: "webinarTopic",
  // NOTE: OneDrive drive items (files & folders).
  itemId: "itemName",
  fileId: "fileName",
  folderId: "folderName",
  parentFolderId: "parentFolderName",
  destinationFolderId: "destinationFolderName",
  onedriveItemId: "onedriveFileName",
  // NOTE: Outlook / Exchange / Gmail messages & mail folders. Teams chat messages have no
  // subject, so they name the target with a short `messagePreview` snippet instead — hence
  // two candidates for the same id key.
  messageId: ["messageSubject", "messagePreview"],
  outlookMessageId: "messageSubject",
  mailFolderId: "mailFolderDisplayName",
  // NOTE: Gmail threads & drafts — named by their subject. (Gmail's `labelId` is
  // covered by the Linear `labelId: "labelName"` entry below — same counterpart key.)
  threadId: "threadSubject",
  draftId: "draftSubject",
  // NOTE: Microsoft Calendar events & calendars.
  eventId: "eventSubject",
  calendarId: "calendarDisplayName",
  // NOTE: Linear projects, assignees, labels, views, leads.
  linearProjectId: "linearProjectName",
  assigneeId: "assigneeName",
  labelId: "labelName",
  viewId: "viewName",
  leadId: "leadName",
  // NOTE: Call assistant meetings.
  meetingUuid: "meetingTitle",
  calendarEventUuid: "meetingTitle",
  // NOTE: Automations (cron / hook / webhook / workflow) addressed by UUID. `name` is what
  // the tool passes; `taskTitle` is what the server enriches cron approvals with from the
  // DB (_enrichCronJobDisplayArgs), which is why this ID needs two candidates.
  uuid: ["name", "taskTitle"],
  // NOTE: Calendly event types (the arg is a URI, the sibling names it).
  event_type: "event_type_name",
  // NOTE: Asana addresses everything by GID.
  taskGid: "taskName",
  asanaProjectGid: "asanaProjectName",
  // NOTE: Notion pages & databases are raw UUIDs; Confluence pages share the `pageId` key.
  pageId: "pageTitle",
  parentPageId: "parentPageTitle",
  databaseId: "databaseTitle",
  // NOTE: Linear issues (UUID in the arg, ENG-123 or the title in the counterpart).
  issueId: "issueTitle",
  // NOTE: BambooHR employees — HR actions must name the person, not an internal number.
  employeeId: "employeeName",
  // NOTE: GitLab jobs & pipeline schedules.
  jobId: "jobName",
  scheduleId: "scheduleDescription",
  // NOTE: Zendesk tickets.
  ticketId: "ticketSubject",
} as const;

// NOTE: A PII placeholder in any representation — raw `<PERSON_1>`, markdown-escaped
// `\<PERSON_1\>`, half-escaped `\<PERSON_1>`, or HTML-escaped `&lt;PERSON_1&gt;` (mirrors
// the server's PII_PLACEHOLDER_ANY_REPRESENTATION_REGEX). The approval card receives args
// that were de-anonymized at the egress boundary, but that restoration is fail-open: when
// it fails the raw anonymized args are shown instead. A display name that still holds a
// placeholder is therefore NOT readable, so the ID row it would have replaced must stay.
// No `g` flag — `.test()` only, so it carries no lastIndex state.
export const PII_PLACEHOLDER_VALUE_REGEX =
  /(?:\\<|&lt;|<)[A-Z_]+_\d+(?:\\>|&gt;|>)/;

export const DESTRUCTIVE_KEYWORDS = [
  "delete",
  "trash",
  "remove",
  "cancel",
  "permanentlyDelete",
  "batchDelete",
  "batchTrash",
] as const;

// NOTE: Prefixes that contain destructive keywords but are non-destructive
// (e.g., "untrash" contains "trash" but is a restore operation).
export const NON_DESTRUCTIVE_PREFIXES = [
  "untrash",
  "undelete",
  "restore",
  "unban",
  "unremove",
] as const;

export const FAMILY_BAR_COLORS: Record<TToolFamily, string> = {
  calendar: "bg-primary-dark",
  email: "bg-primary-dark",
  destructive: "bg-destructive",
  document: "bg-primary-dark",
  generic: "bg-primary-dark",
};

export const FAMILY_ICON_COLORS: Record<TToolFamily, string> = {
  calendar: "text-foreground-secondary",
  email: "text-foreground-secondary",
  destructive: "text-destructive",
  document: "text-foreground-secondary",
  generic: "text-foreground-secondary",
};

// NOTE: Maximum character length for content preview in document approval cards
export const CONTENT_PREVIEW_MAX_LENGTH = 300 as const;

// NOTE: Labels used to extract document title from approval rows
export const DOCUMENT_TITLE_LABELS = [
  "fileName",
  "fileId",
  "newName",
  "itemName",
] as const;

export const DOCUMENT_PREFIXES = [
  "createWordDocument",
  "updateWordDocumentContent",
  "appendToWordDocument",
  "exportWordDocumentToPdf",
  "copyWordDocument",
] as const;

export const CALENDAR_PREFIXES = [
  "createCalendarEvent",
  "getCalendarEvents",
  "updateCalendarEvent",
  "deleteCalendarEvent",
  "addMeetLinkToCalendarEvent",
  "createMicrosoftCalendar",
  "listMicrosoftCalendars",
  "getMicrosoftCalendar",
  "searchMicrosoftCalendar",
  "searchMicrosoftCalendarEvent",
  "updateMicrosoftCalendar",
  "deleteMicrosoftCalendar",
  "createMicrosoftCalendarEvent",
  "getMicrosoftCalendarEvent",
  "listMicrosoftCalendarEvents",
  "updateMicrosoftCalendarEvent",
  "deleteMicrosoftCalendarEvent",
  "respondToMicrosoftCalendarEvent",
  "batchDeleteMicrosoftCalendarEvents",
  "batchRespondToMicrosoftCalendarEvents",
  "findMeetingTimes",
  "getFreeBusySchedule",
  "listMicrosoftCalendarPermissions",
  "addMicrosoftCalendarPermission",
  "removeMicrosoftCalendarPermission",
  "forwardMicrosoftCalendarEvent",
  "cancelMicrosoftCalendarEvent",
  "mesListCalendarEvents",
  "mesGetCalendarEvent",
  "mesCreateCalendarEvent",
  "mesUpdateCalendarEvent",
  "mesDeleteCalendarEvent",
  "mesGetUserAvailability",
  // NOTE: Pipedream calendar WRITE tools only, by exact name. delete-event
  // resolves to the destructive family first (getToolFamily), and list-events
  // must stay generic — its args carry no event fields, so the calendar card
  // would render only empty placeholders for it.
  "google_calendar-create-event",
  "google_calendar-update-event",
  // NOTE: Pipedream Outlook Calendar WRITE tools (NSAI-3765, F14). Same rule as
  // the google_calendar entries above: write tools only, by exact name — delete
  // resolves to the destructive family first, and list/get carry no event fields
  // so they must stay generic.
  "microsoft_outlook_calendar-create-calendar-event",
  "microsoft_outlook_calendar-update-calendar-event",
  "microsoft_outlook_calendar-update-recurring-event-instance",
] as const;

export const EMAIL_PREFIXES = [
  "sendOutlook",
  "createOutlookDraft",
  "createOutlookForward",
  "createOutlookReply",
  "forwardOutlookMessage",
  "replyOutlookMessage",
  "replyAllOutlookMessage",
  "batchMoveOutlookMessages",
  "batchCopyOutlookMessages",
  "sendGmailMessage",
  "createGmailDraft",
  "sendGmailDraft",
  "mesSendEmail",
  "mesGetInbox",
  "mesListMailFolders",
  "mesListMessages",
  "mesGetMessage",
  "mesGetMessageByIndex",
  "mesMoveEmail",
  "mesDeleteEmail",
  "mesMarkMessageAsRead",
  "mesFlagMessage",
  // NOTE: Pipedream twins of the native Gmail tools — same email approval card.
  "gmail-send-email",
  "gmail-create-draft",
  // NOTE: Pipedream Outlook mail tools (NSAI-3765, F14). send-email is a combined
  // send/reply/draft tool; the rest are draft/reply. They use recipients/subject/
  // content (not to/subject/body) — the email card renders their args as rows
  // either way, and getEmptyEmailFields reads `content` for them (see below).
  "microsoft_outlook-send-email",
  "microsoft_outlook-create-draft-email",
  "microsoft_outlook-create-draft-reply",
  "microsoft_outlook-reply-to-email",
  // NOTE: NSAI-7339 — Zoho Mail's send and Zoho Desk's ticket email reply. Both
  // are renamed to canonical keys by ZOHO_PIPEDREAM_EMAIL_ARG_ALIASES.
  "zoho_mail-send-email",
  "zoho_desk-send-email-reply",
] as const;

// NOTE: Subset of EMAIL_PREFIXES covering only send/draft operations that carry subject and body.
// Used to scope the empty-content warning to tools that actually compose email content.
export const EMAIL_SEND_PREFIXES = [
  "sendOutlook",
  "createOutlookDraft",
  "createOutlookForward",
  "createOutlookReply",
  "forwardOutlookMessage",
  "replyOutlookMessage",
  "replyAllOutlookMessage",
  "sendGmailMessage",
  "createGmailDraft",
  "sendGmailDraft",
  "mesSendEmail",
  // NOTE: Pipedream twins of the native Gmail send/draft tools (registry props
  // verified: to/cc/bcc/subject/body match what getEmptyEmailFields reads).
  "gmail-send-email",
  "gmail-create-draft",
  // NOTE: Outlook composing tools (NSAI-3765, F14) — carry `subject` + `content`,
  // so the empty-content guard applies. getEmptyEmailFields reads `content` (not
  // `body`) for these, and treats send-email as a reply (no warning) when
  // `inReplyToMessageId` is present. Reply-only tools go in
  // EMAIL_REPLY_FORWARD_PREFIXES below instead.
  "microsoft_outlook-send-email",
  "microsoft_outlook-create-draft-email",
  // NOTE: NSAI-7339 — composes a new email with `subject` + `content`.
  "zoho_mail-send-email",
] as const;

// NOTE: Outlook reply/forward tools reply to or forward an existing email.
// They use `comment` instead of subject+body, and an empty comment is intentional
// (e.g. forwarding without adding a note), so no empty-content warning is needed.
export const EMAIL_REPLY_FORWARD_PREFIXES = [
  "createOutlookForward",
  "createOutlookReply",
  "forwardOutlookMessage",
  "replyOutlookMessage",
  "replyAllOutlookMessage",
  // NOTE: Pipedream Outlook reply / draft-reply (NSAI-3765, F14) — reply to an
  // existing message; an empty body is intentional, so no empty-content warning.
  "microsoft_outlook-reply-to-email",
  "microsoft_outlook-create-draft-reply",
] as const;

// NOTE: These tools send an already-composed draft; they carry no subject/body args.
export const EMAIL_SEND_DRAFT_PREFIXES = [
  "sendGmailDraft",
  "sendOutlookDraftMessage",
] as const;
